/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Stack,
} from '@mui/material';
import { AreaFilter } from '../components/ProductDashboard/AreaFilter';
import { SRSearchBar } from '../components/ProductDashboard/SRSearchBar';
import { SRGroupedList } from '../components/ProductDashboard/SRGroupedList';
import { EmptyState } from '../components/ProductDashboard/EmptyState';
import { useProductTimeline, useAreas } from '../hooks/useProductTimeline';
import { groupSRsByExpectedVersion } from '../utils/grouping';
import { filterSRs } from '../utils/filtering';

export const ProductDashboardPage: React.FC = () => {
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Fetch available areas
  const { data: areasData } = useAreas();

  // Preselect the first area when areas are loaded
  useEffect(() => {
    if (areasData?.areas && areasData.areas.length > 0 && !selectedArea) {
      setSelectedArea(areasData.areas[0]);
    }
  }, [areasData?.areas, selectedArea]);

  // Fetch SR delivery data
  const {
    data: timelineData,
    isLoading,
    error,
  } = useProductTimeline({
    area: selectedArea || undefined,
  });

  // Filter SRs by search query
  const filteredSRs = useMemo(() => {
    if (!timelineData?.items) return [];
    return filterSRs(timelineData.items, searchQuery);
  }, [timelineData?.items, searchQuery]);

  // Group filtered SRs by expected version
  const groupedSRs = useMemo(() => {
    return groupSRsByExpectedVersion(filteredSRs);
  }, [filteredSRs]);

  // Filter out empty groups
  const visibleGroups = useMemo(() => {
    return groupedSRs.filter((group) => group.items.length > 0);
  }, [groupedSRs]);

  // Check if we have search results
  const hasSearchResults = searchQuery.trim() !== '' && visibleGroups.length === 0;

  return (
    <Box sx={{ pb: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Product Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View SR tickets (product requirements) and their delivery status by release
          </Typography>
        </Box>

        {/* Filters section */}
        <Paper
          sx={{
            p: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <Grid container spacing={2} alignItems="center" sx={{ mx: 0, width: '100%' }}>
            <Grid item xs={12} md={6}>
              <AreaFilter
                selectedArea={selectedArea}
                onChange={setSelectedArea}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SRSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Loading state */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error">
            Failed to load product dashboard data: {error.message}
          </Alert>
        )}

        {/* Empty state: no search results */}
        {!isLoading && !error && hasSearchResults && (
          <EmptyState
            type="no-search-results"
            onClearSearch={() => setSearchQuery('')}
          />
        )}

        {/* Empty state: no SRs for selected area */}
        {!isLoading && !error && !hasSearchResults && visibleGroups.length === 0 && (
          <EmptyState type="no-srs" />
        )}

        {/* Grouped SR List */}
        {!isLoading && !error && visibleGroups.length > 0 && (
          <Box>
            <SRGroupedList groups={visibleGroups} />
          </Box>
        )}
      </Stack>
    </Box>
  );
};
